import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { reportsApi } from '@/api/reports';
import { AppDialogLayout, StepTrail } from '@/components/system';
import { Button } from '@/components/ui/button';
import { SelectableOption } from '@/components/ui/selectable-option';
import { resolvePatientAge } from '@/utils/resolvePatientAge';
import {
    BASIC_QUESTIONS,
    CHILD_BEHAVIOR_GROUPS,
    MENTAL_STATE_GROUPS,
    UNDER_12_OPTIONS,
    buildSurveySteps,
    getAgeGroupFromPatient,
    hasAnySelections,
    hasSurveyData,
    toTrailSteps,
    toggleArrayValue,
    type BasicField,
    type ChildField,
    type MentalField,
    type Under12Type,
} from './behavior-observation/survey-model';
import SurveyGeneratingState from './behavior-observation/SurveyGeneratingState';
import SurveyNotesField from './behavior-observation/SurveyNotesField';
import SurveyQuestionGroups from './behavior-observation/SurveyQuestionGroups';
import type { BehaviorObservationSurvey } from '@/types/report';

const DIALOG_NAME = 'Questionário de Observação do Comportamento';
const DETAIL_QUESTION_ID = 'behavior-observation-detail-question';

interface BehaviorObservationSurveyModalProps {
    /** O rascunho que o laudo já carrega em memória. */
    localSurvey?: BehaviorObservationSurvey;
    /** Quando presente, o modal busca o rascunho salvo antes de abrir o primeiro passo. */
    reportId?: string;
    patientAge?: number;
    patientBirthDate?: string;
    examDate?: string;
    onServerSurveyLoaded?: (survey: BehaviorObservationSurvey) => void;
    onCancel: () => void;
    onSubmit: (survey: BehaviorObservationSurvey) => Promise<void>;
}

const emptySurvey = (
    ageGroup: BehaviorObservationSurvey['age_group'],
    initial?: BehaviorObservationSurvey,
): BehaviorObservationSurvey => ({
    age_group: ageGroup,
    under_12_types: initial?.under_12_types || [],
    over_12_needs_mental_state: initial?.over_12_needs_mental_state ?? null,
    basic: initial?.basic || {},
    child_behavior: initial?.child_behavior || {},
    mental_state: initial?.mental_state || {},
    general_notes: initial?.general_notes || '',
});

export default function BehaviorObservationSurveyModal({
    localSurvey,
    reportId,
    patientAge,
    patientBirthDate,
    examDate,
    onServerSurveyLoaded,
    onCancel,
    onSubmit,
}: BehaviorObservationSurveyModalProps) {
    const guessedAge = useMemo(
        () =>
            resolvePatientAge({
                dateOfBirth: patientBirthDate,
                examDate,
                fallbackAge: patientAge,
            }),
        [examDate, patientBirthDate, patientAge],
    );
    const inferredAgeGroup = getAgeGroupFromPatient(guessedAge) || 'over_12';

    const [survey, setSurvey] = useState<BehaviorObservationSurvey>(() =>
        emptySurvey(inferredAgeGroup, localSurvey),
    );
    const [currentStep, setCurrentStep] = useState(0);
    const [isLoading, setIsLoading] = useState(Boolean(reportId));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);

    // O rascunho salvo é buscado aqui, e não por quem abre o modal, para que
    // fechar durante a busca já baste como cancelamento: sem o modal montado não
    // há resposta atrasada para descartar.
    const onServerSurveyLoadedRef = useRef(onServerSurveyLoaded);
    onServerSurveyLoadedRef.current = onServerSurveyLoaded;
    const hasLocalDraft = useMemo(() => hasSurveyData(localSurvey), [localSurvey]);

    useEffect(() => {
        if (!reportId) return;

        let active = true;

        (async () => {
            try {
                const response = await reportsApi.getBehaviorObservations(reportId);
                if (!active) return;

                // O rascunho em memória vence o salvo: ele é o mais recente
                // sempre que a pessoa já mexeu no questionário nesta sessão.
                const serverSurvey = response.data?.payload;
                if (serverSurvey && !hasLocalDraft) {
                    setSurvey(emptySurvey(inferredAgeGroup, serverSurvey));
                    onServerSurveyLoadedRef.current?.(serverSurvey);
                }
            } catch {
                if (active) toast.error('Erro ao carregar o questionário.');
            } finally {
                if (active) setIsLoading(false);
            }
        })();

        return () => {
            active = false;
        };
    }, [reportId, inferredAgeGroup, hasLocalDraft]);

    const steps = useMemo(() => buildSurveySteps(survey), [survey]);
    const trail = useMemo(() => toTrailSteps(steps), [steps]);

    useEffect(() => {
        if (currentStep > steps.length - 1) {
            setCurrentStep(Math.max(steps.length - 1, 0));
        }
    }, [currentStep, steps.length]);

    const currentStepData = steps[currentStep];
    // Um passo de ramificação nunca é o fim, mesmo sendo o último da lista:
    // responder a ele acrescenta os passos que ele escolhe. Sem isso, o adulto
    // que ainda não escolheu o detalhamento via "Gerar texto" numa tela onde a
    // única coisa a fazer era continuar.
    const isBranchStep =
        currentStepData?.id === 'under12-types' || currentStepData?.id === 'over12-detail';
    const isLastStep = currentStep === steps.length - 1 && !isBranchStep;
    const isBusy = isLoading || isGenerating;

    const handleUnder12TypeToggle = (value: Under12Type) => {
        setSurvey((prev) => ({
            ...prev,
            under_12_types: toggleArrayValue(prev.under_12_types, value),
        }));
    };

    const handleBasicToggle = (field: BasicField, option: string) => {
        setSurvey((prev) => ({
            ...prev,
            basic: { ...prev.basic, [field]: toggleArrayValue(prev.basic?.[field], option) },
        }));
    };

    const handleChildToggle = (field: ChildField, option: string) => {
        setSurvey((prev) => ({
            ...prev,
            child_behavior: {
                ...prev.child_behavior,
                [field]: toggleArrayValue(prev.child_behavior?.[field], option),
            },
        }));
    };

    const handleMentalToggle = (field: MentalField, option: string) => {
        setSurvey((prev) => ({
            ...prev,
            mental_state: {
                ...prev.mental_state,
                [field]: toggleArrayValue(prev.mental_state?.[field], option),
            },
        }));
    };

    const canProceed = useMemo(() => {
        if (!currentStepData) return false;
        switch (currentStepData.id) {
            case 'under12-types':
                return (survey.under_12_types || []).length > 0;
            case 'over12-detail':
                return survey.over_12_needs_mental_state !== null;
            default:
                return true;
        }
    }, [currentStepData, survey.under_12_types, survey.over_12_needs_mental_state]);

    const handleNext = async () => {
        if (!isLastStep) {
            if (!canProceed) {
                toast.warning('Finalize as seleções antes de continuar.');
                return;
            }
            setCurrentStep((prev) => prev + 1);
            return;
        }

        if (!hasAnySelections(survey)) {
            toast.warning('Selecione pelo menos uma opção para gerar o texto.');
            return;
        }

        setIsSubmitting(true);
        setIsGenerating(true);
        try {
            await onSubmit(survey);
        } catch {
            setIsGenerating(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBack = () => {
        if (currentStep > 0) setCurrentStep((prev) => prev - 1);
    };

    const notesField = (
        <SurveyNotesField
            value={survey.general_notes || ''}
            onChange={(value) => setSurvey((prev) => ({ ...prev, general_notes: value }))}
        />
    );

    const renderStepContent = () => {
        if (!currentStepData) return null;

        switch (currentStepData.id) {
            case 'under12-types':
                return (
                    <div className="space-y-3">
                        {UNDER_12_OPTIONS.map((option) => (
                            <SelectableOption
                                key={option.id}
                                control="checkbox"
                                checked={(survey.under_12_types || []).includes(option.id)}
                                onToggle={() => handleUnder12TypeToggle(option.id)}
                                label={option.title}
                            />
                        ))}
                    </div>
                );
            case 'over12-detail':
                return (
                    <div className="space-y-3">
                        <h4
                            id={DETAIL_QUESTION_ID}
                            className="text-sm font-semibold text-foreground"
                        >
                            {currentStepData.question}
                        </h4>
                        <div
                            role="radiogroup"
                            aria-labelledby={DETAIL_QUESTION_ID}
                            className="grid gap-3 sm:grid-cols-2"
                        >
                            <SelectableOption
                                control="radio"
                                checked={survey.over_12_needs_mental_state === true}
                                onToggle={() =>
                                    setSurvey((prev) => ({
                                        ...prev,
                                        over_12_needs_mental_state: true,
                                    }))
                                }
                                label="Sim, requer observação detalhada do estado mental"
                            />
                            <SelectableOption
                                control="radio"
                                checked={survey.over_12_needs_mental_state === false}
                                onToggle={() =>
                                    setSurvey((prev) => ({
                                        ...prev,
                                        over_12_needs_mental_state: false,
                                    }))
                                }
                                label="Não, apenas observação comportamental básica"
                            />
                        </div>
                    </div>
                );
            case 'basic':
                return (
                    <div className="space-y-6">
                        <SurveyQuestionGroups
                            groups={BASIC_QUESTIONS}
                            values={survey.basic || {}}
                            onToggle={handleBasicToggle}
                        />
                        {notesField}
                    </div>
                );
            case 'child':
                return (
                    <div className="space-y-6">
                        <SurveyQuestionGroups
                            groups={CHILD_BEHAVIOR_GROUPS}
                            values={survey.child_behavior || {}}
                            onToggle={handleChildToggle}
                        />
                        {notesField}
                    </div>
                );
            case 'mental':
                return (
                    <div className="space-y-6">
                        <SurveyQuestionGroups
                            groups={MENTAL_STATE_GROUPS}
                            values={survey.mental_state || {}}
                            onToggle={handleMentalToggle}
                        />
                        {notesField}
                    </div>
                );
            default:
                return null;
        }
    };

    const busyTitle = isGenerating ? 'Gerando observação...' : 'Carregando questionário...';

    // Sem trilha, o nome do questionário é a única identidade que o diálogo tem;
    // com ela, o título nomeia o passo aberto e a trilha diz onde ele fica.
    const title = isBusy
        ? busyTitle
        : steps.length > 1 && currentStepData
          ? currentStepData.title
          : DIALOG_NAME;

    const footer = isBusy ? undefined : (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Button variant="ghost" onClick={onCancel} disabled={isSubmitting}>
                Cancelar
            </Button>
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
                <Button
                    variant="outline"
                    onClick={handleBack}
                    disabled={currentStep === 0 || isSubmitting}
                >
                    Voltar
                </Button>
                <Button onClick={handleNext} disabled={isSubmitting}>
                    {isLastStep ? 'Gerar texto' : 'Continuar'}
                </Button>
            </div>
        </div>
    );

    return (
        <AppDialogLayout
            title={title}
            description={isBusy ? undefined : currentStepData?.description}
            header={
                !isBusy && steps.length > 1 ? (
                    <StepTrail steps={trail} currentIndex={currentStep} />
                ) : undefined
            }
            footer={footer}
        >
            {isBusy ? <SurveyGeneratingState /> : renderStepContent()}
        </AppDialogLayout>
    );
}
